import React, { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { updateUserProfile } from "../../../utils/userApi";
import LocationInput from "../../../helper/locationInput";
import api from "../../../utils/api";
import { toast } from "react-hot-toast";
// ICONS
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaSchool,
  FaMapMarkerAlt,
} from "react-icons/fa";

const PersonalInfoForm = ({ onCancel, setStatusModal }) => {
  const { user, updateUser } = useAuth();
  const [error, setError] = useState("");

  const [phoneOtpStage, setPhoneOtpStage] = useState("idle"); // idle | sent
  const [phoneOtp, setPhoneOtp] = useState("");
  const [phoneOtpLoading, setPhoneOtpLoading] = useState(false);

  const handleSendPhoneOtp = async () => {
    setPhoneOtpLoading(true);
    try {
      await api.post("/users/me/phone/send-otp");
      toast.success("Verification code sent to your phone");
      setPhoneOtpStage("sent");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to send verification code",
      );
    } finally {
      setPhoneOtpLoading(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    setPhoneOtpLoading(true);
    try {
      await api.post("/users/me/phone/verify-otp", { otp: phoneOtp });
      toast.success("Phone verified!");
      updateUser({ ...user, phoneVerified: true });
      setPhoneOtpStage("idle");
      setPhoneOtp("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid or expired code");
    } finally {
      setPhoneOtpLoading(false);
    }
  };

  const isInstitution = user?.role === "institution";

  const [formData, setFormData] = useState({
    // tutor fields
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",

    // institution fields
    academyName: user?.academyName || "",
    address: user?.location?.address || "",

    // shared
    email: user?.email || "",
    phone: user?.phone || "",

    // admin fields
    adminFirstName: user?.adminFirstName || "",
    adminLastName: user?.adminLastName || "",
    adminEmail: user?.adminEmail || "",
    adminPhone: user?.adminPhone || "",
  });

  const validateField = (name, value) => {
    let error = "";

    const isInstitution = user?.role === "institution";

    if (isInstitution) {
      if (name === "academyName" && !value) {
        error = "Training centre name is required";
      }

      if (name === "adminFirstName" && !value) {
        error = "Admin first name is required";
      }

      if (name === "adminEmail" && !value) {
        error = "Admin email is required";
      }
    } else {
      if (name === "firstName" && !value) {
        error = "First name is required";
      }

      if (name === "lastName" && !value) {
        error = "Last name is required";
      }
    }

    if (name === "email" && !value) {
      error = "Email is required";
    }

    if (name === "phone" && !value) {
      error = "Phone is required";
    }

    return error;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateField(e.target.name, e.target.value);

    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");

    try {
      const res = await updateUserProfile(formData, user.token);

      updateUser(res.user); // ✅ update global state

      setStatusModal({
        open: true,
        type: "success",
        message: "Profile Updated",
        subtext: "Your profile has been updated successfully.",
      });
      onCancel();
    } catch (err) {
      console.error(err);
      setStatusModal({
        open: true,
        type: "error",
        message: "Error Updating Profile",
        subtext: "There was an error updating your profile. Please try again.",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="edit-form-card">
      {error && <p className="form-error">{error}</p>}

      {isInstitution ? (
        <>
          <div className="form-group">
            <label>
              <FaSchool /> Training Centre Name
            </label>
            <input
              name="schoolName"
              value={formData.academyName}
              onChange={handleChange}
            />
          </div>

          {/* <div className="form-group"> */}
          {/* <label>Training Center Address</label> */}
          <LocationInput
            formData={formData.address}
            setFormData={setFormData}
          />

          {/* <input
              name="address"
              value={formData.address}
              onChange={handleChange}
            /> */}
          {/* </div> */}
        </>
      ) : (
        <>
          {/* ✅ TUTOR FIELDS */}
          <div className="form-row">
            <div className="form-group">
              <label>
                <FaUser /> First Name
              </label>
              <input
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>
                <FaUser /> Last Name
              </label>
              <input
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
          </div>
        </>
      )}

      {/* ✅ SHARED */}
      <div className="form-group">
        <label>
          <FaEnvelope /> {isInstitution ? "Training Center Email" : "Email"}
        </label>
        <input name="email" value={formData.email} onChange={handleChange} />
      </div>

      <div className="form-group">
        <label>
          <FaPhone /> {isInstitution ? "Training Center Phone" : "Phone"}
        </label>
        <input name="phone" value={formData.phone} onChange={handleChange} />

        {user?.phoneVerified ? (
          <p className="phone-verified-note">✅ Phone verified</p>
        ) : (
          <div className="phone-verify-widget">
            {phoneOtpStage === "idle" ? (
              <button
                type="button"
                className="phone-verify-btn"
                onClick={handleSendPhoneOtp}
                disabled={phoneOtpLoading || !formData.phone?.trim()}
              >
                {phoneOtpLoading ? "Sending…" : "Verify Phone"}
              </button>
            ) : (
              <div className="phone-otp-row">
                <input
                  type="text"
                  placeholder="6-digit code"
                  value={phoneOtp}
                  onChange={(e) => setPhoneOtp(e.target.value)}
                  maxLength={6}
                />
                <button
                  type="button"
                  className="phone-verify-btn"
                  onClick={handleVerifyPhoneOtp}
                  disabled={phoneOtpLoading || phoneOtp.length !== 6}
                >
                  {phoneOtpLoading ? "Verifying…" : "Confirm"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ✅ ADMIN SECTION (ONLY FOR INSTITUTION) */}
      {isInstitution && (
        <>
          <h4 className="form-section-title">Administrator Information</h4>

          <div className="form-row">
            <div className="form-group">
              <label>
                <FaUser /> First Name
              </label>
              <input
                name="adminFirstName"
                value={formData.adminFirstName}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>
                <FaUser /> Last Name
              </label>
              <input
                name="adminLastName"
                value={formData.adminLastName}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>
              <FaEnvelope /> Email
            </label>
            <input
              name="adminEmail"
              value={formData.adminEmail}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>
              <FaPhone /> Phone
            </label>
            <input
              name="adminPhone"
              value={formData.adminPhone}
              onChange={handleChange}
            />
          </div>
        </>
      )}

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="edit-cancel-btn">
          Cancel
        </button>

        <button type="submit" className="edit-primary-btn">
          Save
        </button>
      </div>
    </form>
  );
};

export default PersonalInfoForm;
